import {
  isPublicationErrorCode,
  LOCAL_STORE_NAMESPACE,
  PublicationError,
  type PublicationErrorCode,
} from '@streamlain/doc-publication-contract';
import { localAccountKeyError } from '@streamlain/view-config/local-data-seal';

import { AutoReconnectConnection } from '../../connection';
import type { LocalAccountAuthority } from '../../local-data/key-provider';
import type { DocClock, ListedBlobRecord } from '../../storage';
import type {
  DocPublicationNativeDBApis,
  LocalDataWriteContext,
  NativeJournalCAS,
  NativeLocalDataScope,
  NativePayloadIdentity,
  NativePendingPublication,
  NativeSealedPublication,
  NativeStoredBlob,
  NativeStoredDoc,
} from '../../storage/doc-publication';
import type {
  AuthBoundStorageOptions,
  LocalAuthBinding,
} from '../../storage/local-data-access';
import {
  parseUniversalId,
  type SpaceType,
  universalId,
} from '../../utils/universal-id';
import { SqliteEncryption, SqliteKeyCASConflict } from './encryption';

export function sqlitePublicationErrorCode(
  error: unknown
):
  | PublicationErrorCode
  | 'KEY_UNAVAILABLE'
  | 'DENIED'
  | 'CORRUPT'
  | 'LOCAL_STORE_NEWER'
  | 'KEY_CAS_CONFLICT'
  | undefined {
  if (typeof error !== 'object' || error === null) return;
  const record = error as { message?: unknown; code?: unknown };
  const message = typeof record.message === 'string' ? record.message : '';
  const candidate = message.startsWith('DOC_PUBLICATION:')
    ? message.slice('DOC_PUBLICATION:'.length)
    : record.code;
  if (
    candidate === 'DENIED' ||
    candidate === 'KEY_UNAVAILABLE' ||
    candidate === 'CORRUPT' ||
    candidate === 'LOCAL_STORE_NEWER' ||
    candidate === 'KEY_CAS_CONFLICT'
  )
    return candidate;
  return isPublicationErrorCode(candidate) ? candidate : undefined;
}
export function sqlitePublicationError(error: unknown): Error {
  const code = sqlitePublicationErrorCode(error);
  if (code === 'KEY_CAS_CONFLICT') return new SqliteKeyCASConflict();
  if (
    code === 'DENIED' ||
    code === 'KEY_UNAVAILABLE' ||
    code === 'CORRUPT' ||
    code === 'LOCAL_STORE_NEWER'
  )
    return localAccountKeyError(code);
  return code
    ? new PublicationError(code)
    : new Error('DOC_PUBLICATION:UNKNOWN');
}

export interface SqliteNativeDBOptions extends AuthBoundStorageOptions {
  readonly flavour: string;
  readonly type: SpaceType;
  readonly id: string;
}

export type UnboundSqliteNativeDBOptions = Omit<
  SqliteNativeDBOptions,
  'authBinding'
>;

export function encodeSqliteAccountKey(accountKey: string): string {
  return encodeURIComponent(accountKey).replace(
    /[*!'()~]/g,
    char => '%' + char.charCodeAt(0).toString(16).toUpperCase()
  );
}

export interface SqliteAccountNamespace {
  readonly accountKey: string;
}

export function sqliteNamespaceForAccountKey(accountKey: string): string {
  if (!accountKey) throw new PublicationError('LOCKED');
  return `${LOCAL_STORE_NAMESPACE}:${encodeSqliteAccountKey(accountKey)}`;
}

export function sqliteNamespace(binding: LocalAuthBinding | null): string {
  if (binding === undefined) throw new PublicationError('LOCKED');
  return sqliteNamespaceForAccountKey(binding?.accountId ?? 'local');
}

export function sqlitePoolKey(
  id: string,
  binding: LocalAuthBinding | null
): string {
  return `${id}@${sqliteNamespace(binding)}${binding ? '#' + binding.authEpoch : ''}`;
}

/** Parse only canonical namespace/epoch keys emitted by sqlitePoolKey. */
export function parseSqlitePoolKey(key: string): {
  universalId: string;
  namespace: string;
  authEpoch: number | null;
} {
  try {
    const marker = '@' + LOCAL_STORE_NAMESPACE + ':';
    const split = key.lastIndexOf(marker);
    if (split <= 0) throw new PublicationError('LOCKED');
    const id = key.slice(0, split);
    if (universalId(parseUniversalId(id)) !== id)
      throw new PublicationError('LOCKED');
    const parts = key.slice(split + marker.length).split('#');
    if (parts.length > 2 || !parts[0]) throw new PublicationError('LOCKED');
    const accountKey = decodeURIComponent(parts[0]);
    if (encodeSqliteAccountKey(accountKey) !== parts[0])
      throw new PublicationError('LOCKED');
    let authEpoch: number | null = null;
    if (parts.length === 2) {
      if (!/^[1-9][0-9]*$/.test(parts[1])) throw new PublicationError('LOCKED');
      authEpoch = Number(parts[1]);
      if (!Number.isSafeInteger(authEpoch))
        throw new PublicationError('LOCKED');
    } else if (accountKey !== 'local') throw new PublicationError('LOCKED');
    return {
      universalId: id,
      namespace: sqliteNamespaceForAccountKey(accountKey),
      authEpoch,
    };
  } catch {
    throw new PublicationError('LOCKED');
  }
}

import type { PortableExportNativeApis } from '../../storage/portable-export';

export interface NativeDBApis
  extends DocPublicationNativeDBApis, PortableExportNativeApis {
  getPendingPublication(
    id: string,
    binding: LocalAuthBinding | null,
    operationId: string
  ): Promise<NativePendingPublication | null>;
  readLocalDataScopes(
    id: string,
    binding: LocalAuthBinding | null,
    scopes: readonly string[]
  ): Promise<readonly NativeLocalDataScope[]>;
  listBlobMetadata(
    id: string
  ): Promise<
    readonly Pick<NativeStoredBlob, 'key' | 'metadata' | 'payloadIdentity'>[]
  >;
  listPendingPublications(
    id: string,
    binding: LocalAuthBinding | null,
    after: number,
    limit: number
  ): Promise<readonly NativePendingPublication[]>;
  beginPublicationAttempt(
    id: string,
    binding: LocalAuthBinding | null,
    input: Omit<
      import('@streamlain/doc-publication-contract').BeginPublicationAttemptInput,
      'send'
    > & {
      send?: import('@streamlain/doc-publication-contract').PublicationSendPolicy;
    },
    cas?: NativeJournalCAS,
    context?: LocalDataWriteContext
  ): Promise<NativePendingPublication>;
  acknowledgePublication(
    id: string,
    binding: LocalAuthBinding | null,
    input: import('@streamlain/doc-publication-contract').AcknowledgePublicationInput,
    context?: LocalDataWriteContext
  ): Promise<void>;
  applyRemotePublication(
    id: string,
    binding: LocalAuthBinding | null,
    input: import('@streamlain/doc-publication-contract').ApplyRemotePublicationInput & {
      views: readonly {
        members: readonly (import('@streamlain/doc-publication-contract').DocPublicationView['members'][number] & {
          payloadIdentity?: NativePayloadIdentity;
        })[];
        viewId: string;
      }[];
    },
    context?: LocalDataWriteContext
  ): Promise<
    import('@streamlain/doc-publication-contract').RemotePublicationLocalReceipt
  >;

  listRefusedPublications: (
    id: string,
    binding: LocalAuthBinding | null,
    docIds: readonly string[]
  ) => Promise<
    readonly import('@streamlain/doc-publication-contract').RefusedPublication[]
  >;
  resolveRefusedPublications: (
    id: string,
    binding: LocalAuthBinding | null,
    docIds: readonly string[],
    resolution: import('@streamlain/doc-publication-contract').RefusedPublicationResolution,
    context?: LocalDataWriteContext
  ) => Promise<readonly string[]>;
  commitPublication: (
    id: string,
    binding: LocalAuthBinding | null,
    input: NativeSealedPublication,
    context?: LocalDataWriteContext
  ) => Promise<
    import('@streamlain/doc-publication-contract').LocalPublicationReceipt & {
      applied: boolean;
    }
  >;
  connect: (id: string, binding: LocalAuthBinding | null) => Promise<void>;
  /** Monotonic raise of the bound account's auth mirror; never lowers it. */
  raiseAuthState: (
    id: string,
    authEpoch: number,
    logoutPending: boolean
  ) => Promise<void>;
  /**
   * Raises the auth mirror of every open native database of the account; not
   * bound to one database, so only raiseNativeAccountMirrors calls it.
   */
  raiseAccountAuthState: (
    accountId: string,
    authEpoch: number,
    logoutPending: boolean
  ) => Promise<void>;
  disconnect: (id: string) => Promise<void>;
  pushUpdate: (
    id: string,
    docId: string,
    update: Uint8Array,
    identity?: NativePayloadIdentity,
    context?: LocalDataWriteContext
  ) => Promise<Date>;
  getDocSnapshot: (
    id: string,
    docId: string
  ) => Promise<NativeStoredDoc | null>;
  setDocSnapshot: (
    id: string,
    snapshot: NativeStoredDoc,
    context?: LocalDataWriteContext
  ) => Promise<boolean>;
  getDocUpdates: (id: string, docId: string) => Promise<NativeStoredDoc[]>;
  markUpdatesMerged: (
    id: string,
    docId: string,
    updates: Date[],
    revisions?: string[],
    context?: LocalDataWriteContext
  ) => Promise<number>;
  deleteDoc: (
    id: string,
    docId: string,
    context?: LocalDataWriteContext
  ) => Promise<void>;
  getDocClocks: (id: string, after?: Date | null) => Promise<DocClock[]>;
  getDocClock: (id: string, docId: string) => Promise<DocClock | null>;
  getBlob: (id: string, key: string) => Promise<NativeStoredBlob | null>;
  setBlob: (
    id: string,
    blob: NativeStoredBlob,
    context?: LocalDataWriteContext
  ) => Promise<void>;
  deleteBlob: (
    id: string,
    key: string,
    permanently: boolean,
    context?: LocalDataWriteContext
  ) => Promise<void>;
  releaseBlobs: (id: string, context?: LocalDataWriteContext) => Promise<void>;
  listBlobs: (id: string) => Promise<ListedBlobRecord[]>;
  getPeerRemoteClocks: (id: string, peer: string) => Promise<DocClock[]>;
  getPeerRemoteClock: (
    id: string,
    peer: string,
    docId: string
  ) => Promise<DocClock | null>;
  setPeerRemoteClock: (
    id: string,
    peer: string,
    docId: string,
    clock: Date
  ) => Promise<void>;
  getPeerPulledRemoteClocks: (id: string, peer: string) => Promise<DocClock[]>;
  getPeerPulledRemoteClock: (
    id: string,
    peer: string,
    docId: string
  ) => Promise<DocClock | null>;
  setPeerPulledRemoteClock: (
    id: string,
    peer: string,
    docId: string,
    clock: Date
  ) => Promise<void>;
  getPeerPushedClocks: (id: string, peer: string) => Promise<DocClock[]>;
  getPeerPushedClock: (
    id: string,
    peer: string,
    docId: string
  ) => Promise<DocClock | null>;
  setPeerPushedClock: (
    id: string,
    peer: string,
    docId: string,
    clock: Date
  ) => Promise<void>;
  clearClocks: (id: string) => Promise<void>;
  /** null: no local copy yet. 0: documents stored without a version. */
  getRecoveryVersion: (id: string) => Promise<number | null>;
  setRecoveryVersion: (id: string, version: number) => Promise<void>;
  setBlobUploadedAt: (
    id: string,
    peer: string,
    blobId: string,
    uploadedAt: Date | null
  ) => Promise<void>;
  getBlobUploadedAt: (
    id: string,
    peer: string,
    blobId: string
  ) => Promise<Date | null>;
}

type NativeDBApisWrapper = NativeDBApis extends infer APIs
  ? {
      [K in keyof APIs]: APIs[K] extends (...args: any[]) => any
        ? Parameters<APIs[K]> extends [string, ...infer Rest]
          ? (...args: Rest) => ReturnType<APIs[K]>
          : never
        : never;
    }
  : never;

// raiseAuthState only ever locks: it runs while a connection opens, before
// its one primary account read has passed.
const ungatedNativeApis = new Set<keyof NativeDBApis>([
  'connect',
  'disconnect',
  'exportAbort',
  'raiseAuthState',
]);

let apis: NativeDBApis | null = null;

export function bindNativeDBApis(a: NativeDBApis) {
  apis = a;
}

/** Raises the auth mirror of every open native database of the account (Desktop logout and login). */
export async function raiseNativeAccountMirrors(
  accountId: string,
  authority: LocalAccountAuthority
) {
  if (!apis) throw new Error('Not in native context.');
  await apis.raiseAccountAuthState(
    accountId,
    authority.authEpoch,
    authority.logoutPending
  );
}

export class NativeDBConnection extends AutoReconnectConnection<{
  connected: true;
}> {
  readonly apis: NativeDBApisWrapper;
  readonly encryption: SqliteEncryption;
  disconnected: Promise<void> = Promise.resolve();

  readonly flavour = this.options.flavour;
  readonly type = this.options.type;
  readonly id = this.options.id;

  constructor(private readonly options: SqliteNativeDBOptions) {
    super();

    if (!apis) {
      throw new Error('Not in native context.');
    }

    this.apis = this.warpApis(apis);
    this.encryption = new SqliteEncryption(options, this);
  }

  override get shareId(): string {
    return this.poolKey;
  }

  get poolKey() {
    return sqlitePoolKey(
      universalId({ peer: this.flavour, type: this.type, id: this.id }),
      this.options.authBinding
    );
  }

  warpApis(originalApis: NativeDBApis): NativeDBApisWrapper {
    const id = this.poolKey;
    return new Proxy(
      {},
      {
        get: (_target, key: keyof NativeDBApisWrapper) => {
          const v = originalApis[key];
          if (typeof v !== 'function') return undefined;

          return async (...args: any[]) => {
            try {
              // The native call checks the auth_state mirror itself; this only
              // refuses a connection that never passed its account read or
              // heard of a newer epoch. Connection and export-temp cleanup
              // stay reachable when locked.
              if (!ungatedNativeApis.has(key))
                this.encryption.provider?.assertAuthority();
              return await v.call(
                originalApis,
                id,
                // @ts-expect-error I don't know why it complains ts(2556)
                ...args
              );
            } catch (error) {
              throw sqlitePublicationError(error);
            }
          };
        },
      }
    ) as unknown as NativeDBApisWrapper;
  }

  override async doConnect() {
    this.encryption.validate();
    await this.disconnected;
    await this.apis.connect(this.options.authBinding);
    try {
      // The one primary account read of this connection.
      await this.encryption.provider?.connect(authority =>
        this.apis.raiseAuthState(authority.authEpoch, authority.logoutPending)
      );
    } catch (error) {
      await this.apis.disconnect();
      throw error;
    }
    return { connected: true as const };
  }

  override doDisconnect() {
    this.encryption.dispose();
    this.disconnected = this.apis.disconnect();
    void this.disconnected.catch(error => {
      console.error(
        'NativeDBConnection close failed',
        sqlitePublicationErrorCode(error) ?? 'UNKNOWN'
      );
    });
  }
}
